/**
 * Utilitário para derivar uma paleta completa a partir de uma cor primária hex.
 * Usado pelo BrandThemeProvider para gerar CSS variables de marca.
 */

export interface BrandPalette {
  primary: string
  primaryDim: string
  primaryGlow: string
  primaryGlowStrong: string
  primaryForeground: string
  /** Fundo do item de menu ativo, a marca clareada até virar superfície. */
  navActiveBg: string
  /** Rótulo sobre esse fundo, a mesma matiz, escura o bastante pra ler. */
  navActiveFg: string
}

/**
 * Tema da marca como ele chega ao provider: só a cor primária é obrigatória
 *, os tons derivados dela (`Partial<BrandPalette>`) são recalculados por
 * `themeToStyleVars`, e as superfícies são overrides opcionais.
 */
export interface FullBrandTheme extends Partial<BrandPalette> {
  primary: string
  bg?: string
  bgElevated?: string
  surface1?: string
  surface2?: string
  surface3?: string
  surface4?: string
  surfaceHover?: string
}

/** Converte hex para HSL space-separated (formato Tailwind) */
export function hexToHsl(hex: string): string {
  hex = hex.replace("#", "")
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("")
  }

  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  if (max === min) {
    return `0 0% ${Math.round(l * 100)}%`
  }

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

  let h = 0
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6
      break
    case g:
      h = ((b - r) / d + 2) / 6
      break
    case b:
      h = ((r - g) / d + 4) / 6
      break
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/** Converte hex para componentes RGB */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  hex = hex.replace("#", "")
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("")
  }
  return {
    r: parseInt(hex.substring(0, 2), 16),
    g: parseInt(hex.substring(2, 4), 16),
    b: parseInt(hex.substring(4, 6), 16),
  }
}

/** Calcula luminância relativa (WCAG) */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex)
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Recompõe a cor mantendo matiz e saturação e trocando só a luminosidade.
 *
 * É como a rampa da marca é construída no globals.css (hue fixo, L variando):
 * clarear assim mantém o parentesco com a cor original, enquanto misturar
 * com branco no espaço RGB lava a matiz e devolve um tom acinzentado.
 */
function withLightness(hex: string, lightness: number): string {
  const [h, s] = hexToHsl(hex).split(" ")
  return `${h} ${s} ${lightness}%`
}

/** Gera uma versão mais escura/desaturada da cor (dim) */
function dimColor(hex: string): string {
  const { r, g, b } = hexToRgb(hex)
  const factor = 0.65
  return `#${[r, g, b]
    .map((c) =>
      Math.round(c * factor)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`
}

/**
 * Gera paleta completa a partir de uma cor primária hex.
 * Se for uma cor clara, o foreground será preto. Se escura, branco.
 */
export function derivePalette(primaryHex: string): BrandPalette {
  const { r, g, b } = hexToRgb(primaryHex)
  const lum = relativeLuminance(primaryHex)
  const dimHex = dimColor(primaryHex)

  return {
    primary: hexToHsl(primaryHex),
    primaryDim: hexToHsl(dimHex),
    primaryGlow: `rgba(${r}, ${g}, ${b}, 0.15)`,
    primaryGlowStrong: `rgba(${r}, ${g}, ${b}, 0.30)`,
    primaryForeground: lum > 0.179 ? "0 0% 0%" : "0 0% 100%",
    // 82% / 14% é o mesmo par que a rampa padrão usa (lime-200 sobre
    // lime-900), dá ~11:1 em qualquer matiz.
    navActiveBg: withLightness(primaryHex, 82),
    navActiveFg: withLightness(primaryHex, 14),
  }
}

/**
 * A cor que a API devolve para quem NUNCA personalizou o app.
 *
 * E o lime do tema antigo, e nao o do design v2 (`#CBFA4B`): por isso ela
 * significa "nao escolheu cor", e nao "escolheu esta cor".
 */
export const COR_PADRAO_DO_WELLTRAINER = "#D7F205"

/** Escurece ou clareia um hex por um fator, mantendo o matiz. */
function mexerNoBrilho(hex: string, fator: number): string {
  const { r, g, b } = hexToRgb(hex)
  const ajustar = (c: number) =>
    Math.round(Math.min(255, Math.max(0, fator >= 1 ? c + (255 - c) * (fator - 1) : c * fator)))
  return `#${[r, g, b].map((c) => ajustar(c).toString(16).padStart(2, "0")).join("")}`
}

/** Hex a partir de HSL com uma luminosidade nova (para a versão lavada). */
export function hexComLuminosidade(hex: string, luminosidade: number): string {
  const [h, s] = hexToHsl(hex).split(" ")
  const hh = parseFloat(h) / 360
  const ss = parseFloat(s) / 100
  const ll = luminosidade / 100
  const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss
  const pp = 2 * ll - q
  const canal = (t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return pp + (q - pp) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return pp + (q - pp) * (2 / 3 - t) * 6
    return pp
  }
  const [r, g, b] = [canal(hh + 1 / 3), canal(hh), canal(hh - 1 / 3)]
  return `#${[r, g, b]
    .map((c) =>
      Math.round(c * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`
}

/**
 * O acento do design v2 (`--d-acento*`) na cor de quem personalizou o app.
 *
 * Existe porque o v2 tem a sua propria paleta, separada dos tokens antigos: sem
 * isto, a pessoa trocava a cor, salvava, e o painel continuava lime. O que muda
 * e so o ACENTO; fundo, texto e bordas continuam sendo os do tema, porque
 * repintar superficie por cor de cliente e o caminho mais curto para uma tela
 * ilegivel.
 *
 * `fg` sai da luminancia da cor, e nao fixo em preto: um acento escuro com texto
 * preto em cima nao se le. E o `soft` (fundo lavado de selo e de estado) muda
 * por tema, porque no escuro "lavado" quer dizer mais escuro, e nao mais claro.
 */
export function acentoDaMarca(
  primaryHex: string | null | undefined,
  tema: "claro" | "escuro",
): Record<string, string> | undefined {
  if (!primaryHex) return undefined
  const hex = "#" + primaryHex.replace("#", "")

  /* Sem cor propria, nada e trocado: o v2 fica com a paleta dele.
     Isto NAO e detalhe. A cor que a API devolve para quem nunca personalizou e
     `#D7F205`, o lime do tema ANTIGO, enquanto o v2 usa `#CBFA4B`. Sem esta
     saida, todo personal que nunca mexeu em nada teria o painel repintado num
     lime ligeiramente diferente do que foi desenhado. */
  if (hex.toUpperCase() === COR_PADRAO_DO_WELLTRAINER) return undefined

  const claro = tema === "claro"
  return {
    "--d-acento": hex,
    // No claro o hover escurece; no escuro ele clareia, que e a direcao que se
    // percebe como "mais aceso" sobre fundo escuro.
    "--d-acento-hover": mexerNoBrilho(hex, claro ? 0.9 : 1.1),
    "--d-acento-ativo": mexerNoBrilho(hex, claro ? 0.8 : 0.9),
    "--d-acento-fg": relativeLuminance(hex) > 0.179 ? "#0B0B0B" : "#FFFFFF",
    "--d-acento-soft": hexComLuminosidade(hex, claro ? 90 : 14),
    /* O destino atual da navegacao. No CLARO ele e hex no tema (nao da para sair
       do acento por `color-mix` sem perder o contraste do rotulo), entao precisa
       ser derivado aqui: e o mesmo par 82/14 que o menu antigo usa em
       `navActiveBg`/`navActiveFg`, que rende ~9,4:1 em qualquer matiz.

       No ESCURO nao entra nada de proposito: la o tema aponta a var para
       `--d-acento-soft` e `--d-acento-texto`, que ja estao na cor da marca. */
    ...(claro
      ? {
          "--d-nav-atual-bg": hexComLuminosidade(hex, 82),
          "--d-nav-atual-fg": hexComLuminosidade(hex, 14),
        }
      : {}),
  }
}

/**
 * Converte um FullBrandTheme em um objeto de CSS custom properties
 * pronto para ser injetado como inline style.
 */
export function themeToStyleVars(theme: FullBrandTheme): Record<string, string> {
  const vars: Record<string, string> = {}
  const palette = derivePalette("#" + theme.primary.replace("#", ""))

  // Cores derivadas da primária
  vars["--primary"] = palette.primary
  vars["--primary-foreground"] = palette.primaryForeground
  vars["--primary-dim"] = palette.primaryDim
  vars["--primary-glow"] = palette.primaryGlow
  vars["--primary-glow-strong"] = palette.primaryGlowStrong
  vars["--nav-active-bg"] = palette.navActiveBg
  vars["--nav-active-fg"] = palette.navActiveFg

  // Atualizar aliases que dependem da primary
  vars["--ring"] = palette.primary
  vars["--sidebar-primary"] = palette.primary
  vars["--sidebar-primary-foreground"] = palette.primaryForeground

  // Atualizar shadows com a nova cor
  const { r, g, b } = hexToRgb(theme.primary)
  vars["--shadow-glow"] = `0 0 20px rgba(${r}, ${g}, ${b}, 0.15)`
  vars["--shadow-glow-lg"] = `0 0 40px rgba(${r}, ${g}, ${b}, 0.30)`

  // Overrides opcionais de surfaces
  if (theme.bg) vars["--bg"] = hexToHsl(theme.bg)
  if (theme.bgElevated) vars["--bg-elevated"] = hexToHsl(theme.bgElevated)
  if (theme.surface1) {
    vars["--surface-1"] = hexToHsl(theme.surface1)
    vars["--card"] = hexToHsl(theme.surface1)
  }
  if (theme.surface2) {
    vars["--surface-2"] = hexToHsl(theme.surface2)
    vars["--popover"] = hexToHsl(theme.surface2)
    vars["--input"] = hexToHsl(theme.surface2)
  }
  if (theme.surface3) {
    vars["--surface-3"] = hexToHsl(theme.surface3)
    vars["--secondary"] = hexToHsl(theme.surface3)
    vars["--muted"] = hexToHsl(theme.surface3)
    vars["--accent"] = hexToHsl(theme.surface3)
  }
  if (theme.surface4) vars["--surface-4"] = hexToHsl(theme.surface4)
  if (theme.surfaceHover) vars["--surface-hover"] = hexToHsl(theme.surfaceHover)

  return vars
}
